import {Component, Inject} from '@angular/core';
import {UserService} from "../../../services/user.service";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {
  AbstractProdForm,
  EmailValidator,
  ProdFormButton,
  ProdFormField,
  ProdFormFieldType,
  ProdFormValidators,
  RequiredValidator
} from 'prod-form';
import {ProdDialogData, ProdDialogService} from 'prod-dialog';
import {UserGroupService} from "../../../services/user-group.service";
import {OrganizationService} from "../../../services/organization.service";
import {Role} from "../../../dip/role";
import {RoleService} from "../../../services/role.service";
import {OrganizationTreeSelect} from "../../../dip/organization-tree-select";
import {DipColumn} from "../../../dip/dip-column";
import {ProdGridColumnType, FormEvent} from 'prod-grid';
import {HttpFilterParam} from 'prod-shared';
import {AuthenticationService} from "../../../services/authentication.service";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html'
})
export class UserFormComponent extends AbstractProdForm {
  title: string = this.translate.instant('user.form.title');
  private isFieldsEditable: boolean = this.roleService.hasRole([Role.ROLE_USER_EDIT, Role.ROLE_DISTRIBUTOR_ADMIN]);
  fields: ProdFormField[] = [
    ProdFormField.create({
      type: ProdFormFieldType.TEXT,
      name: 'username',
      label: this.translate.instant('user.form.username'),
      validators: new ProdFormValidators([
        new RequiredValidator(), new EmailValidator()
      ]),
      isEditable: this.isFieldsEditable
    }),
    ProdFormField.create({
      type: ProdFormFieldType.TEXT,
      name: 'firstName',
      label: this.translate.instant('user.form.first_name'),
      validators: new ProdFormValidators([
        new RequiredValidator(),
      ]),
      isEditable: this.isFieldsEditable
    }),
    ProdFormField.create({
      type: ProdFormFieldType.TEXT,
      name: 'lastName',
      label: this.translate.instant('user.form.last_name'),
      validators: new ProdFormValidators([
        new RequiredValidator()
      ]),
      isEditable: this.isFieldsEditable
    }),
    ProdFormField.create({
      type: ProdFormFieldType.GRID,
      name: 'groups',
      label: this.translate.instant('user.form.groups'),
      validators: new ProdFormValidators([
        new RequiredValidator()
      ]),
      gridParameter: {
        displayColumn: DipColumn.get({property: 'name', type: ProdGridColumnType.STRING}),
        service: UserGroupService,
        extraColumns: [
          DipColumn.get({property: 'organizationUserGroup.organization.name', type: ProdGridColumnType.STRING}),
        ]
      },
      isEditable: this.isFieldsEditable,
      readonly: !this.organizationTreeSelect.company
    }),
    ProdFormField.create({
      type: ProdFormFieldType.GRID,
      name: 'notifierOrganizations',
      label: this.translate.instant('user.form.notifier_organizations'),
      gridParameter: {
        displayColumn: DipColumn.get({property: 'organization.name', type: ProdGridColumnType.STRING}),
        extraColumns: [
          DipColumn.get({property: 'name', type: ProdGridColumnType.STRING})
        ],
        service: OrganizationService
      },
      isEditable: this.isFieldsEditable || this.isSelf(),
      readonly: !this.organizationTreeSelect.company
    })
  ];
  formButtons: ProdFormButton[] = [
    new ProdFormButton({
      id: 'reset-password-button',
      text: this.translate.instant('user.form.reset_password_button'),
      clickEvent: this.resetPassword.bind(this)
    })
  ];

  constructor(
    public dialogRef: MatDialogRef<UserFormComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: ProdDialogData,
    override dataService: UserService,
    private dialogService: ProdDialogService,
    private organizationTreeSelect: OrganizationTreeSelect,
    private roleService: RoleService,
    private authService: AuthenticationService,
    private translate: TranslateService) {
    super();
  }

  ngOnInit() {
    this.formButtons[0].disabled = !this.isFieldsEditable || this.data.records && this.data.records.getSize() > 1 || !this.data.records;
    if (this.organizationTreeSelect.company) {
      this.fields.push(
        ProdFormField.create({
          type: ProdFormFieldType.HIDDEN,
          name: 'organization',
          label: '',
          value: this.organizationTreeSelect.company.id
        })
      );

      const groups = this.fields.find(field => field.name === 'groups');
      const notifierOrganizations = this.fields.find(field => field.name === 'notifierOrganizations');
      groups.gridParameter.filter = new HttpFilterParam({
        property: 'organizationUserGroup.organization',
        operator: 'eq',
        value: this.organizationTreeSelect.company.id
      });
      notifierOrganizations.gridParameter.filter = new HttpFilterParam({
        property: 'organization',
        operator: 'eq',
        value: this.organizationTreeSelect.company.id
      });
    }
  }

  resetPassword() {
    let userId: number;
    this.data.records.getAll().forEach(r => userId = r.id);
    this.dataService.newPasswordRequest(userId).subscribe(res => {
      if (res && res['success']) {
        this.dialogService.alert({
          message: this.translate.instant('user.form.reset_password.success_message'),
          title: this.translate.instant('general.success')
        })
      }
    });
  }

  isSelf() {
    if (this.dialogData.data.records.getSize() !== 1 || this.isFieldsEditable) return false;
    let userId: number = null;
    this.dialogData.data.records.getAll().forEach(user => userId = user.id);
    return this.authService.currentUserValue.id === userId;
  }

  override patch(data) {
    if (this.isSelf()) delete data.organization;
    const patchFunction = this.isSelf() ? 'patchNotifierOrganization' : 'prodGridPatch';
    let allRecordCount = this.data.records.getAll().size;
    this.data.records.getAll().forEach(record => {
      let entityValues = Object.assign({}, data);
      this.dataService[patchFunction](this.getValues(entityValues, record), record.id).subscribe(res => {
        allRecordCount--;
        if (allRecordCount === 0) {
          this.data.formResponseEvent.emit(new FormEvent(res['data']));
        }
      });
    });
  }

}
